import { useCallback, useContext, useEffect, useState } from "react";
import { container } from "tsyringe";
import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import QuestionResponseResource from "common/resources/Project/Config/Vote/QuestionResponseResource";
import VoteResultResponseResource from "common/resources/Vote/VoteResultResponseResource";
import { UserContext } from "contexts/User";
import VoteService from "services/VoteService";
import QuestionService from "services/projects/QuestionService";

const voteService = container.resolve(VoteService);
const questionService = container.resolve(QuestionService);

export type VoteWithResults = {
	question: QuestionResponseResource;
	results: ReadonlyArray<VoteResultResponseResource>;
};

export const useVote = (project: Pick<ProjectResponseResource, "id">) => {
	const { user } = useContext(UserContext);
	const [generalVotes, setGeneralVotes] = useState<ReadonlyArray<VoteWithResults>>([]);
	const [castingVotes, setCastingVotes] = useState<ReadonlyArray<VoteWithResults>>([]);

	const getVoteByQuestionId = useCallback(
		(question: Pick<QuestionResponseResource, "id">) => {
			const allVotes = [...generalVotes, ...castingVotes];
			return allVotes.find((vote) => vote.question.id === question.id);
		},
		[generalVotes, castingVotes],
	);

	const getVoteWithResults = async (question: QuestionResponseResource): Promise<VoteWithResults> => {
		return voteService
			.getVoteResults(question.id)
			.then((results) => results ?? [])
			.then((results) => ({ question, results }));
	};

	const userHasVoted = useCallback(
		(question: Pick<QuestionResponseResource, "id">) => {
			const vote = getVoteByQuestionId(question);
			if (!user || !vote) return false;
			return VoteService.userHasVoted(user.id, vote.question);
		},
		[getVoteByQuestionId, user],
	);

	const fetchGeneralVotes = useCallback(async () => {
		questionService.getAllGeneral(project.id).then((questions) => {
			Promise.all(questions.map(getVoteWithResults)).then(setGeneralVotes);
		});
	}, [project]);

	const fetchCastingVotes = useCallback(async () => {
		questionService.getAllCasting(project.id).then((questions) => {
			Promise.all(questions.map(getVoteWithResults)).then(setCastingVotes);
		});
	}, [project]);

	useEffect(() => {
		Promise.all([fetchGeneralVotes(), fetchCastingVotes()]);
	}, [fetchGeneralVotes, fetchCastingVotes]);

	return {
		generalVotes,
		castingVotes,
		fetchGeneralVotes,
		fetchCastingVotes,
		userHasVoted,
	};
};
